/**
 * Shared Utilities Module
 * Common functions used across the theme JavaScript
 * 
 * @version 1.0.0
 */

const ThemeUtils = (function() {
  'use strict';

  /* =============================================================================
     CURRENCY & FORMATTING
     ============================================================================= */

  /**
   * Format money value to currency string
   * @param {number|string} cents - Amount in cents or decimal
   * @param {string} currency - Currency code (default: USD)
   * @param {string} locale - Locale for formatting (default: en-US)
   * @returns {string} Formatted currency string
   */
  function formatMoney(cents, currency, locale) {
    if (cents === null || cents === undefined) return '';

    // Default to the active Shopify storefront currency/locale when not provided
    const shopifyCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const shopifyLocale = (window.Shopify && window.Shopify.locale) || 'en-US';
    const resolvedCurrency = currency || shopifyCurrency;
    const resolvedLocale = locale || shopifyLocale;

    const amount = typeof cents === 'string' ? parseFloat(cents) : cents;

    // Shopify returns cents, but some APIs return decimal - detect and handle
    const finalAmount = amount > 1000 ? amount / 100 : amount;

    try {
      return new Intl.NumberFormat(resolvedLocale, {
        style: 'currency',
        currency: resolvedCurrency
      }).format(finalAmount);
    } catch (error) {
      return `${finalAmount.toFixed(2)} ${resolvedCurrency}`;
    }
  }

  /**
   * Format number with locale separators
   * @param {number} num - Number to format
   * @param {string} locale - Locale for formatting
   * @returns {string} Formatted number string
   */
  function formatNumber(num, locale = 'en-US') {
    return new Intl.NumberFormat(locale).format(num);
  }

  /* =============================================================================
     DEBOUNCE & THROTTLE
     ============================================================================= */

  /**
   * Debounce function execution
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @param {boolean} immediate - Execute immediately on first call
   * @returns {Function} Debounced function
   */
  function debounce(func, wait = 300, immediate = false) {
    let timeout;
    
    return function executedFunction(...args) {
      const context = this;
      
      const later = function() {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      
      if (callNow) func.apply(context, args);
    };
  }

  /**
   * Throttle function execution
   * @param {Function} func - Function to throttle
   * @param {number} limit - Time limit in milliseconds
   * @returns {Function} Throttled function
   */
  function throttle(func, limit = 100) {
    let inThrottle;
    
    return function executedFunction(...args) {
      const context = this;
      
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /* =============================================================================
     FETCH UTILITIES
     ============================================================================= */

  /**
   * Fetch with AbortController support and error handling
   * @param {string} url - URL to fetch
   * @param {Object} options - Fetch options
   * @param {AbortController} controller - Optional AbortController
   * @returns {Promise<Object>} Response data
   */
  async function fetchWithAbort(url, options = {}, controller = null) {
    const abortController = controller || new AbortController();
    const { timeout = 10000, ...fetchOptions } = options;
    
    const timeoutId = setTimeout(() => abortController.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: abortController.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return await response.text();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Request was cancelled');
      }
      
      throw error;
    }
  }

  /**
   * Fetch JSON with automatic retries
   * @param {string} url - URL to fetch
   * @param {Object} options - Fetch options
   * @param {number} retries - Number of retries
   * @returns {Promise<Object>} Response data
   */
  async function fetchJSON(url, options = {}, retries = 2) {
    for (let i = 0; i <= retries; i++) {
      try {
        const response = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            ...options.headers
          },
          ...options
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        if (i === retries) throw error;
        await wait(1000 * (i + 1)); // Exponential backoff
      }
    }
  }

  /* =============================================================================
     DOM UTILITIES
     ============================================================================= */

  /**
   * Wait for specified milliseconds
   * @param {number} ms - Milliseconds to wait
   * @returns {Promise<void>}
   */
  function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Wait for DOM element to exist
   * @param {string} selector - CSS selector
   * @param {number} timeout - Max wait time in ms
   * @returns {Promise<Element>} The found element
   */
  function waitForElement(selector, timeout = 5000) {
    return new Promise((resolve, reject) => {
      const element = document.querySelector(selector);
      if (element) {
        return resolve(element);
      }

      const observer = new MutationObserver((mutations, obs) => {
        const element = document.querySelector(selector);
        if (element) {
          obs.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`Element ${selector} not found within ${timeout}ms`));
      }, timeout);
    });
  }

  /**
   * Check if element is in viewport
   * @param {Element} element - DOM element to check
   * @param {number} threshold - Visibility threshold (0-1)
   * @returns {boolean} Whether element is in viewport
   */
  function isInViewport(element, threshold = 0) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    
    const vertInView = (rect.top <= windowHeight * (1 - threshold)) && 
                       ((rect.top + rect.height) >= windowHeight * threshold);
    const horInView = (rect.left <= windowWidth * (1 - threshold)) && 
                      ((rect.left + rect.width) >= windowWidth * threshold);
    
    return vertInView && horInView;
  }

  /**
   * Trap focus within an element (for modals/drawers)
   * @param {Element} element - Container element
   * @returns {Function} Cleanup function
   */
  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    function handleKeydown(event) {
      if (event.key !== 'Tab') return;
      
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
    
    element.addEventListener('keydown', handleKeydown);
    
    // Return cleanup function
    return () => element.removeEventListener('keydown', handleKeydown);
  }

  /* =============================================================================
     STRING UTILITIES
     ============================================================================= */

  /**
   * Strip HTML tags from string
   * @param {string} html - HTML string
   * @returns {string} Plain text
   */
  function stripHtml(html) {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  }

  /**
   * Truncate string with ellipsis
   * @param {string} str - String to truncate
   * @param {number} length - Max length
   * @param {string} suffix - Suffix to add (default: ...)
   * @returns {string} Truncated string
   */
  function truncate(str, length = 100, suffix = '...') {
    if (!str || str.length <= length) return str || '';
    return str.substring(0, length).trim() + suffix;
  }

  /**
   * Convert string to kebab-case
   * @param {string} str - String to convert
   * @returns {string} kebab-case string
   */
  function toKebabCase(str) {
    return str
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase();
  }

  /* =============================================================================
     URL UTILITIES
     ============================================================================= */

  /**
   * Get URL parameter value
   * @param {string} name - Parameter name
   * @param {string} url - URL to parse (default: current URL)
   * @returns {string|null} Parameter value
   */
  function getUrlParam(name, url = window.location.href) {
    const urlObj = new URL(url);
    return urlObj.searchParams.get(name);
  }

  /**
   * Update URL parameter without page reload
   * @param {string} key - Parameter name
   * @param {string} value - Parameter value
   */
  function updateUrlParam(key, value) {
    const url = new URL(window.location);
    
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    
    window.history.pushState({}, '', url);
  }

  /* =============================================================================
     EVENT UTILITIES
     ============================================================================= */

  /**
   * Dispatch custom event
   * @param {string} eventName - Event name
   * @param {Object} detail - Event detail data
   * @param {Element} target - Target element (default: document)
   */
  function dispatchEvent(eventName, detail = {}, target = document) {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: true
    });
    target.dispatchEvent(event);
  }

  /**
   * Add event listener with automatic cleanup
   * @param {Element} element - Target element
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   * @param {Object} options - Event listener options
   * @returns {Function} Cleanup function
   */
  function addEventListenerWithCleanup(element, event, handler, options = {}) {
    element.addEventListener(event, handler, options);
    return () => element.removeEventListener(event, handler, options);
  }

  /* =============================================================================
     BREAKPOINT UTILITIES
     ============================================================================= */

  /**
   * Standard breakpoints (matching typography-system.css)
   */
  const BREAKPOINTS = {
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    '2xl': 1400
  };

  /**
   * Check if viewport matches breakpoint
   * @param {string} breakpoint - Breakpoint name (sm, md, lg, xl, 2xl)
   * @param {string} direction - 'up' (min-width) or 'down' (max-width)
   * @returns {boolean} Whether viewport matches
   */
  function matchesBreakpoint(breakpoint, direction = 'up') {
    const value = BREAKPOINTS[breakpoint];
    if (!value) return false;
    
    if (direction === 'up') {
      return window.innerWidth >= value;
    }
    return window.innerWidth < value;
  }

  /**
   * Get current breakpoint name
   * @returns {string} Current breakpoint name
   */
  function getCurrentBreakpoint() {
    const width = window.innerWidth;
    
    if (width >= BREAKPOINTS['2xl']) return '2xl';
    if (width >= BREAKPOINTS.xl) return 'xl';
    if (width >= BREAKPOINTS.lg) return 'lg';
    if (width >= BREAKPOINTS.md) return 'md';
    if (width >= BREAKPOINTS.sm) return 'sm';
    return 'xs';
  }

  /* =============================================================================
     STORAGE UTILITIES
     ============================================================================= */

  /**
   * Safe localStorage get with JSON parsing
   * @param {string} key - Storage key
   * @param {*} defaultValue - Default value if not found
   * @returns {*} Stored value or default
   */
  function getStorage(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      return defaultValue;
    }
  }

  /**
   * Safe localStorage set with JSON stringify
   * @param {string} key - Storage key
   * @param {*} value - Value to store
   * @returns {boolean} Success status
   */
  function setStorage(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Remove item from localStorage
   * @param {string} key - Storage key
   */
  function removeStorage(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      // Silent fail
    }
  }

  /* =============================================================================
     PRODUCT IMAGE UTILITIES
     ============================================================================= */

  /**
   * Get product image URL from various data structures
   * @param {Object} product - Product object
   * @returns {string} Image URL or empty string
   */
  function getProductImageUrl(product) {
    if (!product) return '';
    
    let imageUrl = '';
    
    // Try featured_image first
    if (product.featured_image) {
      if (typeof product.featured_image === 'object' && product.featured_image.url) {
        imageUrl = product.featured_image.url;
      } else if (typeof product.featured_image === 'string') {
        imageUrl = product.featured_image;
      }
    }
    // Try image
    else if (product.image) {
      if (typeof product.image === 'object' && product.image.url) {
        imageUrl = product.image.url;
      } else if (typeof product.image === 'string') {
        imageUrl = product.image;
      }
    }
    // Try images array
    else if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      if (typeof firstImage === 'object') {
        imageUrl = firstImage.src || firstImage.url || '';
      } else if (typeof firstImage === 'string') {
        imageUrl = firstImage;
      }
    }
    
    // Ensure URL has protocol
    if (imageUrl && !imageUrl.startsWith('http')) {
      imageUrl = 'https:' + imageUrl;
    }
    
    return imageUrl;
  }

  /**
   * Get product price from various data structures
   * @param {Object} product - Product object
   * @returns {number|null} Price in cents or null
   */
  function getProductPrice(product) {
    if (!product) return null;
    
    if (product.price) return product.price;
    if (product.variants && product.variants.length > 0) {
      return product.variants[0].price;
    }
    return null;
  }

  /* =============================================================================
     IMAGE LOADING UTILITIES
     ============================================================================= */

  /**
   * Initialize image loading states for elements with shimmer-loader
   * Adds 'is-loaded' class to parent when image loads
   * @param {HTMLElement|Document} container - Container to search for images
   */
  function initImageLoading(container = document) {
    const shimmerContainers = container.querySelectorAll('.shimmer-loader');
    
    shimmerContainers.forEach(shimmer => {
      const parent = shimmer.parentElement;
      if (!parent || parent.classList.contains('is-loaded')) return;
      
      const img = parent.querySelector('img');
      if (!img) return;
      
      // Already loaded (cached)
      if (img.complete && img.naturalHeight !== 0) {
        parent.classList.add('is-loaded');
        return;
      }
      
      // Wait for load
      img.addEventListener('load', () => {
        parent.classList.add('is-loaded');
      }, { once: true });
      
      // Handle error - still hide shimmer
      img.addEventListener('error', () => {
        parent.classList.add('is-loaded');
      }, { once: true });
    });
  }

  /* =============================================================================
     PUBLIC API
     ============================================================================= */

  return {
    // Currency & Formatting
    formatMoney,
    formatNumber,
    
    // Debounce & Throttle
    debounce,
    throttle,
    
    // Fetch Utilities
    fetchWithAbort,
    fetchJSON,
    
    // DOM Utilities
    wait,
    waitForElement,
    isInViewport,
    trapFocus,
    
    // String Utilities
    stripHtml,
    truncate,
    toKebabCase,
    
    // URL Utilities
    getUrlParam,
    updateUrlParam,
    
    // Event Utilities
    dispatchEvent,
    addEventListenerWithCleanup,
    
    // Breakpoint Utilities
    BREAKPOINTS,
    matchesBreakpoint,
    getCurrentBreakpoint,
    
    // Storage Utilities
    getStorage,
    setStorage,
    removeStorage,
    
    // Product Utilities
    getProductImageUrl,
    getProductPrice,
    
    // Image Loading
    initImageLoading
  };
})();

// Make available globally
window.ThemeUtils = ThemeUtils;

// Auto-initialize image loading on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ThemeUtils.initImageLoading());
} else {
  ThemeUtils.initImageLoading();
}
